import { ReactNode } from 'react';
import { makeStyles } from 'tss-react/mui';
import { getColors, typography, spacing } from '../tokens';
import { PrimaryGradientDefs } from './PrimaryGradientDefs';

export interface StatCardProps {
  label: string;
  value: string | number;
  highlighted?: boolean;
  icon?: ReactNode;
}

const GRADIENT_ID = 'stat-card-icon-gradient';

const useStyles = makeStyles<{ highlighted: boolean }>()((
  theme,
  { highlighted },
) => {
  const c = getColors(theme.palette.mode);
  const isLight = theme.palette.mode === 'light';
  const fgAlpha = (a: number) =>
    isLight ? `rgba(0, 0, 0, ${a})` : `rgba(255, 255, 255, ${a})`;
  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: `${spacing(2)}px ${spacing(2.5)}px`,
      borderRadius: 12,
      background: highlighted ? c.background.elevated : c.background.paper,
      border: `1px solid ${highlighted ? fgAlpha(0.2) : fgAlpha(0.1)}`,
      minWidth: 140,
      transition: 'background 0.2s ease, border-color 0.2s ease',
      position: 'relative',
      overflow: 'hidden',
    },
    iconCorner: {
      position: 'absolute',
      top: 12,
      right: 14,
      '& svg': {
        width: 22,
        height: 22,
        '& path': {
          fill: `url(#${GRADIENT_ID})`,
        },
      },
    },
    iconBackground: {
      position: 'absolute',
      bottom: -8,
      right: -4,
      opacity: 0.08,
      transform: 'rotate(-20deg)',
      pointerEvents: 'none',
      '& svg': {
        width: 64,
        height: 64,
        '& path': {
          fill: c.text.primary,
        },
      },
    },
    label: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: 12,
      fontWeight: 400,
      color: c.text.secondary,
      marginBottom: 4,
      lineHeight: 1.3,
      letterSpacing: '0.02em',
      position: 'relative',
      zIndex: 1,
    },
    value: {
      fontFamily: typography.fontFamilyCorpoATextCondensed,
      fontSize: 28,
      fontWeight: 700,
      color: c.text.primary,
      lineHeight: 1.1,
      letterSpacing: '-0.01em',
      position: 'relative',
      zIndex: 1,
    },
  };
});

export const StatCard = ({
  label,
  value,
  highlighted = false,
  icon,
}: Readonly<StatCardProps>) => {
  const { classes } = useStyles({ highlighted });

  return (
    <div className={classes.root}>
      <PrimaryGradientDefs id={GRADIENT_ID} />
      {icon && <div className={classes.iconCorner}>{icon}</div>}
      {icon && <div className={classes.iconBackground}>{icon}</div>}
      <span className={classes.label}>{label}</span>
      <span className={classes.value}>{value}</span>
    </div>
  );
};
